/**
 * OPUS-913 — Badge / StatusPill / permission + entitlement badges.
 * Ported from the Claude Design handoff (app/components.jsx + app/states.jsx).
 * Tone + dot, Title-Case labels; raw status values are preserved by callers.
 */
import React from "react";
import { Icon } from "./Icon";

export const BADGE_TONES = {
  neutral: { bg: "rgba(255,255,255,0.06)", color: "var(--text-muted)", dot: "var(--text-dim)" },
  green: { bg: "rgba(69,255,167,0.12)", color: "var(--green)", dot: "var(--green)" },
  coral: { bg: "rgba(255,69,92,0.13)", color: "var(--coral)", dot: "var(--coral)" },
  amber: { bg: "rgba(255,216,74,0.14)", color: "var(--amber)", dot: "var(--amber)" },
  blue: { bg: "rgba(88,196,255,0.14)", color: "var(--blue)", dot: "var(--blue)" },
  violet: { bg: "rgba(196,69,255,0.15)", color: "var(--violet)", dot: "var(--violet)" },
};

export function Badge({ tone = "neutral", dot = false, icon, children, mono = false, style = {} }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px",
      borderRadius: 999, background: t.bg, color: t.color, fontSize: 11.5, fontWeight: 600,
      fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)", lineHeight: 1.4, whiteSpace: "nowrap", ...style,
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 99, background: t.dot, flexShrink: 0 }} />}
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
}

export const STATUS_TONE = {
  active: "green", online: "green", enabled: "green", connected: "green",
  inactive: "neutral", offline: "neutral", disabled: "neutral", archived: "neutral",
  idle: "amber", draft: "amber", pending: "amber", maintenance: "amber",
  deleted: "coral", error: "coral", failed: "coral",
};
export const STATUS_LABEL = {
  active: "Active", inactive: "Inactive", deleted: "Deleted", pending: "Pending", expired: "Expired",
  online: "Online", offline: "Offline", idle: "Idle", draft: "Draft", archived: "Archived",
  enabled: "Enabled", disabled: "Disabled", error: "Error", maintenance: "Maintenance", connected: "Connected",
};

export function StatusPill({ status }) {
  if (!status) return <span style={{ color: "var(--text-dim)" }}>—</span>;
  const key = String(status).toLowerCase();
  const tone = STATUS_TONE[key] || "neutral";
  const label = STATUS_LABEL[key] || (status.charAt(0).toUpperCase() + status.slice(1));
  return <Badge tone={tone} dot>{label}</Badge>;
}

const PERM_MAP = {
  allowed: { tone: "green", icon: "check", label: "Allowed" },
  disabled: { tone: "neutral", icon: "x", label: "Disabled" },
  locked_role: { tone: "amber", icon: "lock", label: "Locked · role" },
  locked_entitlement: { tone: "amber", icon: "lock", label: "Locked · plan" },
  locked_flag: { tone: "blue", icon: "lock", label: "Locked · flag" },
  denied: { tone: "coral", icon: "shield", label: "Denied" },
  loading: { tone: "neutral", icon: "clock", label: "Checking…" },
};
export function PermissionBadge({ state = "allowed", label }) {
  const p = PERM_MAP[state] || PERM_MAP.allowed;
  return <Badge tone={p.tone} icon={p.icon}>{label || p.label}</Badge>;
}

const ROLE_TONE = { super_admin: "coral", org_admin: "violet", admin: "violet", operator: "blue", designer: "green", tester: "amber", copilot: "neutral" };
const ROLE_LABEL = { super_admin: "Super Admin", org_admin: "Org Admin", admin: "Admin", operator: "Operator", designer: "Designer", tester: "Tester", copilot: "Copilot" };
export function RoleBadge({ role }) {
  const raw = role && role.name ? role.name : role;
  if (!raw || raw === "N/A" || raw === "Unassigned") return <Badge tone="neutral">Unassigned</Badge>;
  const key = String(raw).toLowerCase();
  const label = ROLE_LABEL[key] || (String(raw).charAt(0).toUpperCase() + String(raw).slice(1).replace(/_/g, " "));
  return <Badge tone={ROLE_TONE[key] || "neutral"} dot>{label}</Badge>;
}

export function EntitlementBadge({ tier = "included" }) {
  const map = {
    included: { tone: "green", label: "Included" },
    addon: { tone: "blue", label: "Add-on" },
    not_included: { tone: "neutral", label: "Not in plan" },
    trial: { tone: "violet", label: "Trial" },
  };
  const m = map[tier] || map.included;
  return <Badge tone={m.tone} dot>{m.label}</Badge>;
}

export default Badge;
